/**
 * EnrollmentsOverviewTable
 *
 * Dense table for enrollment overview: provider, payer, type, status, billing-ready.
 * UnifyTable; pagination handled by parent. EnrollmentStatusBadge. PHI-safe.
 */

import { Text } from '@mantine/core';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { Enrollment } from '../types';
import { EnrollmentStatusBadge } from './EnrollmentStatusBadge';

interface EnrollmentsOverviewTableProps {
  enrollments: Enrollment[];
  page: number;
  pageSize: number;
  onRowClick?: (enrollment: Enrollment) => void;
}

function slicePage<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

const COLUMNS: UnifyTableColumn<Enrollment>[] = [
    {
      key: 'provider',
      label: 'Provider',
      render: (e) => (
        <Text size="sm" fw={500}>
          {e.providerName}
        </Text>
      ),
      getValue: (e) => e.providerName,
    },
    {
      key: 'payer',
      label: 'Payer',
      render: (e) => <Text size="sm">{e.payerName}</Text>,
      getValue: (e) => e.payerName,
    },
    {
      key: 'type',
      label: 'Type',
      render: (e) => (
        <Text size="sm" c="dimmed" tt="capitalize">
          {e.enrollmentType.replace('-', ' ')}
        </Text>
      ),
      getValue: (e) => e.enrollmentType,
    },
    {
      key: 'status',
      label: 'Status',
      render: (e) => <EnrollmentStatusBadge status={e.status} />,
      getValue: (e) => e.status,
    },
    {
      key: 'billingReady',
      label: 'Billing-ready',
      render: (e) =>
        e.billingReady ? (
          <IconCircleCheck size={18} aria-label="Billing ready" />
        ) : (
          <IconCircleX size={18} aria-label="Not billing ready" />
        ),
      getValue: (e) => (e.billingReady ? 'yes' : 'no'),
    },
    {
      key: 'nextAction',
      label: 'Next action',
      render: (e) => (
        <Text size="sm" c="dimmed" lineClamp={1}>
          {e.nextAction ?? '—'}
        </Text>
      ),
      getValue: (e) => e.nextAction ?? '',
    },
];

export function EnrollmentsOverviewTable({
  enrollments,
  page,
  pageSize,
  onRowClick,
}: EnrollmentsOverviewTableProps): JSX.Element {
  const rows = useMemo(() => slicePage(enrollments, page, pageSize), [enrollments, page, pageSize]);

  return (
    <UnifyTable
      withPaper={false}
      columns={COLUMNS}
      data={rows}
      getRowKey={(e) => e.id}
      onRowClick={onRowClick}
    />
  );
}
